<template>
  <ValidationObserver ref="loginFormValidator">
    <form
      @submit.prevent="submit"
      data-qa="por-login-form"
    >
      <Input
        id="email"
        validation-rules="required|email"
        :input-model.sync="email"
        :label-text="$t('common_email_label')"
        :placeholder-text="$t('common_email_placeholder')"
        input-type="text"
        :error-text="`${$t('common_validEmail_text')}.`"
        class="por-login-form--wrapper"
        data-qa="por-login-form--email" />
      <Input
        id="password"
        validation-rules="required"
        :trim-input="false"
        :input-model.sync="password"
        :has-error="false"
        :label-text="$t('common_password_text')"
        placeholder-text="••••••••"
        input-type="password"
        :error-text="`${$t('loginForm_emptyPassword_text')}.`"
        class="por-login-form--wrapper"
        data-qa="por-login-form--password" />
      <div class="por-login-form--wrapper">
        <b-button
          size="is-medium"
          type="is-primary"
          expanded
          :disabled="isSubmitting"
          :loading="isSubmitting"
          tag="input"
          native-type="submit"
          :value="$t('loginForm_login_text')"
          data-qa="por-login-form--button" />
      </div>
      <div class="por-login-form--wrapper">
        <b-button tag="router-link"
          class="is-fullwidth"
          :to="{ name: 'forgot-password' }"
          @click.native="forgotPasswordClick"
          data-qa="por-login--form-forgot-password-link"
          type="is-text">
          {{ $t('loginForm_forgotPassword_text') }}
        </b-button>
      </div>
      <p v-if="useGoogleSSO" class="por--divider mt-3 mb-6">{{ $t('registerForm_divider_text') }}</p>
      <div v-if="useGoogleSSO" class="por-login-form--wrapper">
        <GoogleSignInHblButton expanded type="button" @click.native="ssoSubmit(googleProviderName)">{{ $t('loginForm_googleSso_buttonText') }}</GoogleSignInHblButton>
      </div>
    </form>
  </ValidationObserver>
</template>

<script>
import { Auth } from '@aws-amplify/auth'
import { mapGetters } from 'vuex'

import Input from '@/components/inputs/input'

import router from '@/router'
import { redirect } from '@/services/redirect'
import { track, identify } from '@/services/tracking'
import { errorMessageMapping } from '@/helpers/message-mapping'
import { isUnconfirmedLogin } from '@/helpers/error-mapping'
import trackErrors from '@/helpers/track-errors'
import { OAUTH_PROVIDERS } from '@/helpers/constants'
import { ssoSignIn } from '@/helpers/auth-utils'
import GoogleSignInHblButton from '@ui/components/google-button-hbl/GoogleSignInHblButton'

export default {
  name: 'LoginForm',
  data () {
    return {
      email: '',
      googleProviderName: OAUTH_PROVIDERS.GOOGLE_PROVIDER_NAME,
      password: '',
      saeProviderName: OAUTH_PROVIDERS.SAE_PROVIDER_NAME,
      submitStatus: null
    }
  },
  components: {
    Input,
    GoogleSignInHblButton
  },
  computed: {
    validEmail: function () {
      return this.email.toLowerCase()
    },
    isSubmitting: function () {
      return this.submitStatus === 'PENDING'
    },
    ...mapGetters({
      useGoogleSSO: 'site/useGoogleSSO'
    })
  },
  methods: {
    async submit () {
      track({ name: 'button tap', params: { module: 'login', description: 'login button' } })

      this.cognitoResponse = ''

      this.submitStatus = 'PENDING'

      this.$refs.loginFormValidator.validate().then(async success => {
        if (!success) {
          trackErrors(this.$refs.loginFormValidator, 'login')
          this.submitStatus = null

          return
        }

        try {
          this.$store.dispatch('user/setCredentials', { email: this.validEmail, password: '' })
          await Auth.signOut() // In case someone already has tokens on their localStorage
          this.cognitoResponse = await Auth.signIn(this.validEmail, this.password)

          track({ name: 'login success', params: { module: 'login' } })

          let newUser = await Auth.currentSession()
          let patientId = newUser.getIdToken().decodePayload().patient_id

          identify(patientId, { email: this.validEmail }, null)

          this.submitStatus = 'OK'

          if (window.newrelic) {
            window.newrelic.addPageAction('login')
          }
          redirect()
        } catch (error) {
          let errorMessage = errorMessageMapping(error.message)
          if (isUnconfirmedLogin(error)) {
            this.$store.dispatch('user/setCredentials', { email: this.validEmail, password: this.password })
            errorMessage = this.$t('loginForm_emailVerificationRequired_text')

            try { // This try/catch is needed for an edgecase if the user sent out too many resentSignup requests
              await Auth.resendSignUp(this.validEmail)
              router.push({ name: 'email-verification' })
              this.$store.dispatch('site/setMessage', {
                title: this.$t('common_note_text'),
                message: errorMessage,
                type: 'warning'
              })
            } catch (error) {
              this.$store.dispatch('site/setMessage', { message: error.message, type: 'danger' })
              this.submitStatus = null
            }
          } else {
            this.$store.dispatch('site/setMessage', { message: errorMessage, type: 'danger' })
          }

          track({ name: 'error', params: { module: 'login', error_message: errorMessage, description: 'login error' } })
          this.submitStatus = null
        }
      })
    },
    async ssoSubmit (provider) {
      track({ name: 'button tap', params: { module: 'login', description: `${provider} login button` } })
      this.submitStatus = 'PENDING'
      try {
        await ssoSignIn(provider)
        this.submitStatus = 'OK'
      } catch (error) {
        this.$store.dispatch('site/setMessage', { message: error.message, type: 'danger' })
        track({ name: 'error', params: { module: 'login', error_message: error.message, description: `${provider} social sign on error for login` } })
        this.submitStatus = null
      }
    },
    forgotPasswordClick () {
      track({ name: 'button tap', params: { module: 'login', description: 'forgot password link' } })
    }
  }
}
</script>

<style lang="scss">
$por-login-form-field-margin-top: 16px;

$sae-button-color: #005091;
$sae-button-color-alt: #0F62F4;

$sae-button-logo-height: 20px;
$sae-button-logo-margin: 8px;

$sae-button-logo-text-font-size-tablet: 14px;
$sae-button-logo-text-line-height-tablet: 28px;

$sae-button-logo-text-font-size-mobile: 12px;
$sae-button-logo-text-line-height-mobile: 28px;
$sae-button-logo-height-mobile: 14px;
$sae-button-logo-margin-top-mobile: 6px;

.por-login-form--wrapper {
  display: block;
}

.por-login-form--wrapper + .por-login-form--wrapper {
  margin-top: $por-login-form-field-margin-top;
}

button.por-login-form--sae-button {
  color: $sae-button-color;
  border: 2px solid $sae-button-color;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.09);

  height: 45px;
  padding: 12px;
  @media (min-width: 769px) {
    height: 57px;
    padding: 18px;
  }

  &:hover,
  &:active,
  &:focus {
    border: 2px solid $sae-button-color-alt;
    box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.15);
    color: $sae-button-color-alt;
  }
}

.por-login-form--sae-content {
  display: inline-block;
  width: auto;
  margin: auto;
}

.por-login-form--sae-logo {
  margin-top: 4px;
  display: block;
  float: left;
  height: $sae-button-logo-height;
  margin-right: $sae-button-logo-margin;
}

@media(max-width: 1024px) {
  .por-login-form--sae-button-text {
    font-size: $sae-button-logo-text-font-size-tablet;
    line-height: $sae-button-logo-text-line-height-tablet;
  }
}

@media(max-width: 340px) {
  .por-login-form--sae-logo {
    height: $sae-button-logo-height-mobile;
    margin-top: $sae-button-logo-margin-top-mobile;
  }

  .por-login-form--sae-button-text {
    font-size: $sae-button-logo-text-font-size-mobile;
    line-height: $sae-button-logo-text-line-height-mobile;
  }
}
</style>
